import { faChevronDown, faChevronUp } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import clsx from 'clsx';
import { Dropdown, DropdownContent, DropdownTrigger } from 'components/UI/V3/Dropdown';
import { DropdownItem } from 'components/UI/V3/Dropdown/Item/DropdownItem';
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import styles from './PrintRoomSelect.module.scss';

export interface PrintRoomOption {
    id: number;
    label: string;
}

interface PrintRoomSelectProps {
    options: Array<PrintRoomOption>;
    // Selected room ids. An empty array means "All Rooms" (no filter).
    selected: Array<number>;
    onChange: (roomIds: Array<number>) => void;
}

const DROPDOWN_MAX_HEIGHT = 280;
const DROPDOWN_MIN_HEIGHT = 140;
const VIEWPORT_PADDING = 16;
const SIDE_OFFSET = -1;

export const PrintRoomSelect: React.FC<PrintRoomSelectProps> = ({ options, selected, onChange }) => {
    const [open, setOpen] = useState(false);
    const triggerRef = useRef<HTMLDivElement>(null);
    const [triggerWidth, setTriggerWidth] = useState<number>();
    const [side, setSide] = useState<'top' | 'bottom'>('top');
    const [maxHeight, setMaxHeight] = useState(DROPDOWN_MAX_HEIGHT);
    // Nudge DropdownContent's position calc when the chip trigger resizes while open.
    const [sideOffset, setSideOffset] = useState(SIDE_OFFSET);
    const [explicitNone, setExplicitNone] = useState(false);

    const syncDropdownLayout = useCallback(() => {
        if (!triggerRef.current) {
            return;
        }

        const rect = triggerRef.current.getBoundingClientRect();
        setTriggerWidth(rect.width);

        const spaceBelow = window.innerHeight - rect.bottom - VIEWPORT_PADDING;
        const spaceAbove = rect.top - VIEWPORT_PADDING;
        const openUp = spaceAbove >= spaceBelow;
        const available = Math.max(DROPDOWN_MIN_HEIGHT, openUp ? spaceAbove : spaceBelow);

        setSide(openUp ? 'top' : 'bottom');
        setMaxHeight(Math.min(DROPDOWN_MAX_HEIGHT, available));
        // DropdownContent only repositions when its props change; toggle by a sub-pixel
        // so the list stays glued to the trigger as chips wrap/unwrap.
        setSideOffset(current => (current === SIDE_OFFSET ? SIDE_OFFSET - 0.01 : SIDE_OFFSET));
    }, []);

    useLayoutEffect(() => {
        if (!open || !triggerRef.current) {
            return;
        }

        syncDropdownLayout();

        const observer = new ResizeObserver(() => {
            syncDropdownLayout();
        });
        observer.observe(triggerRef.current);

        return () => observer.disconnect();
    }, [open, selected, explicitNone, syncDropdownLayout]);

    useEffect(() => {
        if (selected.length > 0) {
            setExplicitNone(false);
        }
    }, [selected]);

    const allIds = options.map(option => option.id);
    const isAll = !explicitNone && selected.length === 0;
    const isChecked = (id: number) => !explicitNone && (selected.length === 0 || selected.includes(id));

    const toggleAll = (checked: boolean) => {
        setExplicitNone(!checked);
        onChange([]);
    };

    const toggleRoom = (id: number, checked: boolean) => {
        const base = explicitNone ? [] : selected.length === 0 ? allIds : selected;
        const next = new Set(base);
        checked ? next.add(id) : next.delete(id);

        if (next.size === 0) {
            setExplicitNone(true);
            onChange([]);
            return;
        }

        setExplicitNone(false);
        onChange(next.size === allIds.length ? [] : Array.from(next));
    };

    const selectedOptions = isAll || explicitNone ? [] : options.filter(option => selected.includes(option.id));

    const triggerLabel = () => {
        if (isAll) {
            return <span className={styles.value}>All Rooms</span>;
        }

        if (explicitNone || selectedOptions.length === 0) {
            return <span className={styles.placeholder}>Select rooms</span>;
        }

        return (
            <div className={styles.chips}>
                {selectedOptions.map(option => (
                    <span
                        key={option.id}
                        className={styles.chip}
                    >
                        {option.label}
                    </span>
                ))}
            </div>
        );
    };

    return (
        <Dropdown
            open={open}
            onOpenChange={setOpen}
        >
            <DropdownTrigger className={styles.triggerWrapper}>
                <div
                    ref={triggerRef}
                    className={clsx(styles.trigger, {
                        [styles.triggerOpenUp]: open && side === 'top',
                        [styles.triggerOpenDown]: open && side === 'bottom'
                    })}
                >
                    <div className={styles.triggerContent}>{triggerLabel()}</div>
                    <FontAwesomeIcon
                        icon={open ? faChevronUp : faChevronDown}
                        className={styles.chevron}
                    />
                </div>
            </DropdownTrigger>
            <DropdownContent
                className={clsx(styles.dropdownContent, {
                    [styles.dropdownContentUp]: side === 'top',
                    [styles.dropdownContentDown]: side === 'bottom'
                })}
                zIndex={10020}
                side={side}
                align="start"
                sideOffset={sideOffset}
                minWidth={triggerWidth}
                maxWidth={triggerWidth}
            >
                <div
                    className={styles.scrollArea}
                    style={{ maxHeight }}
                >
                    <DropdownItem
                        type="checkbox"
                        checked={isAll}
                        onSelect={toggleAll}
                    >
                        All Rooms
                    </DropdownItem>
                    {options.map(option => (
                        <DropdownItem
                            key={option.id}
                            type="checkbox"
                            checked={isChecked(option.id)}
                            onSelect={checked => toggleRoom(option.id, checked)}
                        >
                            {option.label}
                        </DropdownItem>
                    ))}
                </div>
            </DropdownContent>
        </Dropdown>
    );
};

export default PrintRoomSelect;
